import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '../../redux/hooks';
import Modal from '../../components/modal/Modal';
import FilterDropdown, { FilterOption } from '../../components/custom-input/FilterDropdown';
import DateRangeInput from '../../components/custom-input/DateInputRange';
import PagedTable from '../../components/sorted-table/PagedTable';
import { Loader } from '../../components';
import { AlertSVG, CancelSVG, DownloadSVG } from '../../components/svgs';
import SearchBy from '../../components/search-by/SearchBy';
import InfoWarning from '../../components/info-text/InfoWarning';
import { TransactionFilterOption } from '../../components/transaction/TransactionsList';
import { InventoryTransaction, StandardParamsToODataParams } from '../../types/interfaces';
import { InventorySource } from '../../types/enums';
import { TransactionStatusEnum, TransactionTypeEnum } from '../../types/enums/transaction.enum';
import { downloadInventoryTransactionReport, getAccountTransactionsOdata } from '../../api/transactionsApi';
import {
  downloadOnFarmTransactionHistoryReport,
  getOnFarmAccountTransactionsOdata,
} from '../../services/onFarmTransactionHistory.service';
import { showToast } from '../../services/toast.service';
import { toastMessages } from '../../constants/errorMessages';
import { REACT_APP_ON_FARM_ENABLED } from '../../constants/apiEndpoints';
import {
  LABEL_CONSTANTS,
  NAVIGATION_LINK_CONSTANTS,
  REPORT_CONSTANTS,
  TABLE_HEADER_CONSTANTS,
  VALIDATION_ALERT_CONSTANTS,
  BUTTON_CONSTANTS,
} from '../../constants/common';
import { dateFilter } from '../../utils/dateHandler';
import { dateFormatMMDDYYYY, formatDateToShortMonthDayYear, sortObjectsAlphabetically } from '../../utils/commonUtils';

type TransactionHistoryReportModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type DateRange = {
  startDate: Date | null;
  endDate: Date | null;
};

type SearchItem = {
  id?: string;
  criteria: FilterOption<TransactionFilterOption>;
  query: string;
};

type SearchCondition = {
  condition: string | null;
  shouldFail: boolean;
};

const initialDateRange: DateRange = {
  startDate: null,
  endDate: null,
};

const MOBILE_PREVIEW_PAGE_SIZE = 10;
const MOBILE_BREAKPOINT = 767;

const RSG_EXCLUDED_TRANSACTION_TYPES = new Set([
  TransactionTypeEnum.CheckIn,
  TransactionTypeEnum.CheckOut,
  TransactionTypeEnum.Adjust,
  TransactionTypeEnum.Move,
  TransactionTypeEnum.Reconcile,
]);

const ON_FARM_EXCLUDED_TRANSACTION_TYPES = new Set([
  TransactionTypeEnum.CheckIn,
  TransactionTypeEnum.CheckOut,
  TransactionTypeEnum.Adjust,
  TransactionTypeEnum.Reconcile,
  TransactionTypeEnum.Collect,
  TransactionTypeEnum.Purchase,
]);

const SOURCE_OPTIONS = [
  { source: InventorySource.RSG, label: NAVIGATION_LINK_CONSTANTS.RSG_TRANSACTION_HISTORY },
  ...(REACT_APP_ON_FARM_ENABLED
    ? [{ source: InventorySource.ON_FARM, label: NAVIGATION_LINK_CONSTANTS.ON_FARM_TRANSACTION_HISTORY }]
    : []),
];

const getExcludedTransactionTypes = (inventorySource: InventorySource) =>
  inventorySource === InventorySource.ON_FARM ? ON_FARM_EXCLUDED_TRANSACTION_TYPES : RSG_EXCLUDED_TRANSACTION_TYPES;

const getSearchCriteriaOptions = (isOnFarm: boolean): FilterOption<TransactionFilterOption>[] => [
  {
    name: `${TABLE_HEADER_CONSTANTS.TRANSACTION_ID}`,
    value: { parameter: isOnFarm ? 'onFarmTransactionId' : 'inventoryTransactionId', isNumber: true },
  },
  { name: `${LABEL_CONSTANTS.NOTES}`, value: { parameter: 'notes' } },
  {
    name: `${TABLE_HEADER_CONSTANTS.ANIMAL_CODE}`,
    value: { parameter: isOnFarm ? 'onFarmSpecimen/onFarmAnimal/code' : 'specimen/animal/code' },
  },
  {
    name: `${TABLE_HEADER_CONSTANTS.ANIMAL_NAME}`,
    value: { parameter: isOnFarm ? 'onFarmSpecimen/onFarmAnimal/name' : 'specimen/animal/name' },
  },
  {
    name: `${TABLE_HEADER_CONSTANTS.LOT_NO}`,
    value: { parameter: isOnFarm ? 'onFarmSpecimenId' : 'specimenId', isNumber: true },
  },
];

const getTypeOptions = (inventorySource: InventorySource): FilterOption<string>[] => {
  const excluded = getExcludedTransactionTypes(inventorySource);
  return Object.entries(TransactionTypeEnum)
    .map(([key, value]) => ({
      name: key.replace('Default', 'All'),
      value,
    }))
    .filter(item => !excluded.has(item.value as TransactionTypeEnum));
};

const ON_FARM_EXCLUDED_TRANSACTION_STATUSES = new Set([
  TransactionStatusEnum.Pending,
  TransactionStatusEnum.Hold,
  TransactionStatusEnum.Processed,
]);

const getStatusOptions = (inventorySource: InventorySource): FilterOption<string>[] => {
  return Object.entries(TransactionStatusEnum)
    .map(([key, value]) => ({
      name: key.replace('Default', 'All'),
      value,
    }))
    .filter(
      item =>
        inventorySource !== InventorySource.ON_FARM ||
        !ON_FARM_EXCLUDED_TRANSACTION_STATUSES.has(item.value as TransactionStatusEnum),
    );
};

const PREVIEW_HEADERS = [
  { displayName: `${TABLE_HEADER_CONSTANTS.TRANSACTION_ID}` },
  { displayName: `${TABLE_HEADER_CONSTANTS.ANIMAL}` },
  { displayName: `${TABLE_HEADER_CONSTANTS.TYPE}` },
  { displayName: `${TABLE_HEADER_CONSTANTS.CHANGED_QTY}` },
  { displayName: `${TABLE_HEADER_CONSTANTS.DATE}`, sortValue: 'CreatedDatetime' },
  { displayName: `${TABLE_HEADER_CONSTANTS.STATUS}` },
];

const buildSearchCondition = (search: SearchItem): SearchCondition => {
  const query = search.query?.trim() ?? '';
  const minSearchLength = 2;
  if (query.length < minSearchLength) return { condition: null, shouldFail: false };

  const { parameter, isNumber } = search.criteria.value;

  if (isNumber) {
    const numericValue = +query;
    const isLotNumberSearch = parameter === 'specimenId' || parameter === 'onFarmSpecimenId';

    if (isLotNumberSearch) {
      const lotNumberPath = parameter === 'onFarmSpecimenId' ? 'onFarmSpecimen/lotNumber' : 'specimen/lotNumber';

      if (!Number.isNaN(numericValue)) {
        return {
          condition: `(${parameter} eq ${numericValue} or contains(${lotNumberPath}, '${query}'))`,
          shouldFail: false,
        };
      }

      return { condition: `contains(${lotNumberPath}, '${query}')`, shouldFail: false };
    }

    if (Number.isNaN(numericValue)) return { condition: null, shouldFail: true };

    return { condition: `${parameter} eq ${numericValue}`, shouldFail: false };
  }

  return { condition: `contains(${parameter}, '${query}')`, shouldFail: false };
};

const getReportFilter = (
  searches: SearchItem[],
  selectedType: string,
  selectedStatus: string,
  dateRange: DateRange,
  isOnFarm: boolean,
): string => {
  const filters: string[] = [];

  if (!isOnFarm) {
    filters.push(
      `transactionType ne '${TransactionTypeEnum.CheckIn}'`,
      `transactionType ne '${TransactionTypeEnum.CheckOut}'`,
      `transactionType ne '${TransactionTypeEnum.Adjust}'`,
      `transactionType ne '${TransactionTypeEnum.Move}'`,
    );
  }

  if (selectedType) {
    filters.push(`transactionType eq '${selectedType}'`);
  }

  if (selectedStatus) {
    filters.push(`status eq '${selectedStatus}'`);
  }

  if (dateRange.startDate && dateRange.endDate) {
    filters.push(
      dateFilter(true, {
        startDate: new Date(dateRange.startDate),
        endDate: new Date(dateRange.endDate),
      }),
    );
  }

  const conditionsByParameter: Record<string, string[]> = {};
  const searchedParameters: string[] = [];
  const attemptedNumberParameters: string[] = [];

  for (const search of searches) {
    const { condition, shouldFail } = buildSearchCondition(search);
    const parameter = search.criteria.value.parameter;

    if (shouldFail) {
      if (!attemptedNumberParameters.includes(parameter)) {
        attemptedNumberParameters.push(parameter);
      }
      continue;
    }
    if (!condition) continue;

    if (!conditionsByParameter[parameter]) {
      conditionsByParameter[parameter] = [];
      searchedParameters.push(parameter);
    }
    conditionsByParameter[parameter].push(condition);
  }

  for (const parameter of attemptedNumberParameters) {
    if (!conditionsByParameter[parameter]) return 'false';
  }

  for (const parameter of searchedParameters) {
    filters.push(conditionsByParameter[parameter].join(' or '));
  }

  if (filters.length === 0) return '';
  return `(${filters.join(') and (')})`;
};

type ResultsStatus = 'idle' | 'loading' | 'match' | 'empty';

const getDropdownDisplayName = (value: string | null) => {
  if (value === null) return '';
  return value === '' ? 'All' : value;
};

const buildPreviewRow = (transaction: InventoryTransaction) => [
  transaction.inventoryTransactionId,
  `${transaction.specimen?.animal?.animalId ?? ''} - ${transaction.specimen?.animal?.name ?? ''}`,
  transaction.transactionType,
  transaction.changeQuantity,
  new Date(transaction.createdDatetime).toLocaleDateString(),
  transaction.status,
];

const TransactionHistoryReportModal = ({ isOpen, onClose }: TransactionHistoryReportModalProps): JSX.Element => {
  const activeAccount = useAppSelector(state => state.activeAccount);
  const [inventorySource, setInventorySource] = useState<InventorySource>(InventorySource.RSG);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(initialDateRange);
  const [searches, setSearches] = useState<SearchItem[]>([]);
  const [removedFilter, setRemovedFilter] = useState<SearchItem>();
  const [resetSearchCriteria, setResetSearchCriteria] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDateRangeHighlight, setShowDateRangeHighlight] = useState(false);
  const [resultsStatus, setResultsStatus] = useState<ResultsStatus>('idle');
  const [previewCount, setPreviewCount] = useState(0);
  const [mobilePreviewData, setMobilePreviewData] = useState<InventoryTransaction[]>([]);
  const [isMobilePreviewLoading, setIsMobilePreviewLoading] = useState(false);
  const [isMobileView, setIsMobileView] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
  const mobilePreviewCallNumber = useRef(0);

  const isOnFarm = inventorySource === InventorySource.ON_FARM;
  const searchCriteriaOptions = useMemo(() => getSearchCriteriaOptions(isOnFarm), [isOnFarm]);
  const typeOptions = useMemo(() => getTypeOptions(inventorySource), [inventorySource]);
  const statusOptions = useMemo(() => getStatusOptions(inventorySource), [inventorySource]);
  const transactionInclude = isOnFarm ? 'OnFarmSpecimen.OnFarmAnimal' : 'Specimen.Animal';

  const isDateRangeSelected = Boolean(dateRange.startDate && dateRange.endDate);
  const isLoadingResults = resultsStatus === 'loading';
  const hasMatchingResults = resultsStatus === 'match';
  const isDownloadBlocked = isDownloading || isLoadingResults || !hasMatchingResults;
  const canDownload = !isDownloadBlocked && isDateRangeSelected;
  const hasActiveFilters = searches.length > 0 || Boolean(selectedType) || Boolean(selectedStatus) || isDateRangeSelected;

  const shouldShowResults = isDateRangeSelected;

  const previewFilter = useMemo(() => {
    if (!shouldShowResults) return undefined;
    return getReportFilter(searches, selectedType ?? '', selectedStatus ?? '', dateRange, isOnFarm);
  }, [shouldShowResults, searches, selectedType, selectedStatus, dateRange, isOnFarm]);

  const resetFilters = () => {
    setSelectedType(null);
    setSelectedStatus(null);
    setDateRange(initialDateRange);
    setShowDateRangeHighlight(false);
    setSearches([]);
    setRemovedFilter(undefined);
    setResetSearchCriteria(prev => prev + 1);
    setResultsStatus('idle');
    setPreviewCount(0);
    setMobilePreviewData([]);
    setIsMobilePreviewLoading(false);
  };

  const resetModal = () => {
    setInventorySource(InventorySource.RSG);
    resetFilters();
  };

  const handleSourceChange = (source: InventorySource) => {
    if (source === inventorySource || isDownloading) return;
    setInventorySource(source);
    setSelectedType(null);
    setSelectedStatus(null);
    setSearches([]);
    setRemovedFilter(undefined);
    setResetSearchCriteria(prev => prev + 1);
  };

  const handleClearAll = () => {
    if (isDownloading) return;
    resetFilters();
  };

  const handleRemoveFilter = (index: number) => {
    const updatedSearches = [...searches];
    const removed = updatedSearches.splice(index, 1)[0];
    setRemovedFilter(removed);
    setSearches(updatedSearches);
  };

  const handleClose = () => {
    if (isDownloading) return;
    resetModal();
    onClose();
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isOpen || !shouldShowResults) {
      setResultsStatus('idle');
      setPreviewCount(0);
      setMobilePreviewData([]);
      setIsMobilePreviewLoading(false);
      return;
    }

    setResultsStatus('loading');
    setPreviewCount(0);
  }, [isOpen, shouldShowResults, previewFilter, inventorySource]);

  useEffect(() => {
    if (!isOpen || !shouldShowResults || !previewFilter || !isMobileView) {
      mobilePreviewCallNumber.current += 1;
      setMobilePreviewData([]);
      setIsMobilePreviewLoading(false);
      return;
    }

    const callNum = ++mobilePreviewCallNumber.current;
    setIsMobilePreviewLoading(true);

    const loadMobilePreview = async () => {
      try {
        const getTransactionsOdata = isOnFarm ? getOnFarmAccountTransactionsOdata : getAccountTransactionsOdata;
        const { data } = await getTransactionsOdata(
          StandardParamsToODataParams({
            include: transactionInclude,
            filter: previewFilter,
            sort: '^createdDatetime',
            limit: MOBILE_PREVIEW_PAGE_SIZE,
            offset: 0,
          }),
        );

        if (callNum !== mobilePreviewCallNumber.current) return;

        const rows = Array.isArray(data) ? data : [];
        setMobilePreviewData(rows);
        setPreviewCount(rows.length);
        setResultsStatus(rows.length > 0 ? 'match' : 'empty');
      } catch {
        if (callNum !== mobilePreviewCallNumber.current) return;
        setMobilePreviewData([]);
        setPreviewCount(0);
        setResultsStatus('empty');
      } finally {
        if (callNum === mobilePreviewCallNumber.current) {
          setIsMobilePreviewLoading(false);
        }
      }
    };

    loadMobilePreview();
  }, [isOpen, shouldShowResults, previewFilter, isMobileView, isOnFarm, transactionInclude]);

  const getODataParams = () =>
    StandardParamsToODataParams({
      filter: getReportFilter(searches, selectedType ?? '', selectedStatus ?? '', dateRange, isOnFarm),
      sort: '^CreatedDatetime',
    });

  const generateReportPayload = () => {
    const startDate = formatDateToShortMonthDayYear(dateRange.startDate);
    const endDate = formatDateToShortMonthDayYear(dateRange.endDate);

    return {
      'Transaction Type :': selectedType || undefined,
      'Transaction Status :': selectedStatus || undefined,
      'Report Period :': `${startDate} - ${endDate}`,
    };
  };

  const getReportFileName = () => {
    const prefix = isOnFarm ? 'OnFarmTxns' : 'GeneticTxns';
    const currentDate = dateFormatMMDDYYYY(new Date());
    return activeAccount?.name ? `${prefix}_${activeAccount.name}_${currentDate}.xlsx` : `${prefix}_${currentDate}.xlsx`;
  };

  const handleDownloadReport = async () => {
    if (isDownloadBlocked) return;

    if (!isDateRangeSelected) {
      setShowDateRangeHighlight(true);
      return;
    }

    try {
      setIsDownloading(true);

      const payload = generateReportPayload();
      const query = getODataParams();
      const fileName = getReportFileName();

      if (isOnFarm) {
        await downloadOnFarmTransactionHistoryReport(payload, query, fileName);
        resetModal();
        onClose();
        return;
      }

      const response = await downloadInventoryTransactionReport(payload, query);
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      resetModal();
      onClose();
    } catch {
      showToast.error(toastMessages.FAILED_TO_DOWNLOAD_REPORT);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width={1100} ignoreBackdrop>
      <div className="transaction-history-report-modal">
        <div className="header">
          <h4>{`Download ${REPORT_CONSTANTS.TRANSACTION_HISTORY_REPORT}`}</h4>

          <InfoWarning
            className="transaction-history-report-modal__notice-message"
            infoIconSize="20"
            message={REPORT_CONSTANTS.REPORT_NOTICE}
          />

          <button
            type="button"
            className="transaction-history-report-modal__close"
            onClick={handleClose}
            disabled={isDownloading}>
            <CancelSVG />
          </button>
        </div>

        <div className="body">
          <div className="report-download-instructions">
            <h3>{REPORT_CONSTANTS.GUIDE_TITLE}</h3>
            <ol>
              <li>{REPORT_CONSTANTS.SELECT_DATE_RANGE}</li>
              <li>{REPORT_CONSTANTS.OPTIONAL_FILTERS}</li>
              <li>{REPORT_CONSTANTS.REVIEW_RESULTS}</li>
              <li>{REPORT_CONSTANTS.CLICK_TO_DOWNLOAD}</li>
            </ol>
          </div>

          <hr />

          {REACT_APP_ON_FARM_ENABLED && (
            <div className="transaction-history-report-modal__source-toggle">
              {SOURCE_OPTIONS.map(option => (
                <button
                  key={option.source}
                  type="button"
                  className={`transaction-source-button ${inventorySource === option.source ? 'active' : ''}`}
                  onClick={() => handleSourceChange(option.source)}
                  disabled={isDownloading}>
                  {option.label}
                </button>
              ))}
            </div>
          )}

          <div className="transaction-history-report-modal__filters">
            <div className="filter-category filter-category-search">
              <div className="search-row">
                <div className="search-field">
                  <SearchBy
                    key={inventorySource}
                    searches={searches}
                    setSearches={setSearches}
                    searchCriteriaOptions={searchCriteriaOptions}
                    resetSearchCriteria={resetSearchCriteria}
                    removedFilter={removedFilter}
                  />
                </div>

                {hasActiveFilters && (
                  <button type="button" className="clear-all-button" onClick={handleClearAll} disabled={isDownloading}>
                    <span>{BUTTON_CONSTANTS.CLEAR_ALL}</span>
                    <CancelSVG />
                  </button>
                )}
              </div>

              {searches.length > 0 && (
                <div className="selected-filters-summary">
                  {searches.map((item, index) => (
                    <div key={item.id ?? `${item.criteria.name}-${item.query}-${index}`} className="filter-summary">
                      <span>
                        {item.criteria.name}: <strong>{item.query}</strong>
                      </span>
                      <button
                        type="button"
                        className="remove-filter-btn"
                        onClick={() => handleRemoveFilter(index)}
                        disabled={isDownloading}>
                        <CancelSVG />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-category">
              <label>{TABLE_HEADER_CONSTANTS.TYPE}</label>
              <FilterDropdown
                placeholder="Select"
                options={sortObjectsAlphabetically(typeOptions)}
                value={{ name: getDropdownDisplayName(selectedType), value: selectedType ?? '' }}
                onChange={option => setSelectedType(option.value)}
                disabled={isDownloading}
              />
            </div>

            <div className="filter-category">
              <label>{TABLE_HEADER_CONSTANTS.STATUS}</label>
              <FilterDropdown
                placeholder="Select"
                options={sortObjectsAlphabetically(statusOptions)}
                value={{ name: getDropdownDisplayName(selectedStatus), value: selectedStatus ?? '' }}
                onChange={option => setSelectedStatus(option.value)}
                disabled={isDownloading}
              />
            </div>

            <div className="filter-category">
              <label>
                {TABLE_HEADER_CONSTANTS.DATE_RANGE}
                <span className="required-marker">*</span>
              </label>
              <div className={`date-range-input ${showDateRangeHighlight ? 'highlight' : ''}`}>
                <DateRangeInput
                  value={dateRange}
                  onChange={newDateRange => {
                    setDateRange(newDateRange);
                    setShowDateRangeHighlight(false);
                  }}
                  isClearable
                  rangeLimit={1}
                  disabled={isDownloading}
                  highlightPlaceholder={showDateRangeHighlight}
                />
              </div>
              {showDateRangeHighlight && (
                <div className="validated-input-message-error">
                  <AlertSVG />
                  {VALIDATION_ALERT_CONSTANTS.REQUIRED_FIELD}
                </div>
              )}
            </div>

            {!shouldShowResults && <div className="filters-no-results">{REPORT_CONSTANTS.SELECT_DATE_RANGE_TO_PREVIEW}</div>}
          </div>

          {shouldShowResults && (
            <div className="transaction-history-report-modal__results">
              <div className="results-header">
                <h3>{REPORT_CONSTANTS.RESULTS_PREVIEW}</h3>
                {resultsStatus === 'match' && previewCount > 0 && (
                  <span className="results-count">
                    {previewCount}
                    {previewCount >= 10 ? '+' : ''} shown
                  </span>
                )}
                {resultsStatus === 'empty' && <span className="results-count empty">{LABEL_CONSTANTS.NO_RESULTS_FOUND}</span>}
              </div>

              <div className="desk-report-results">
                <PagedTable
                  key={inventorySource}
                  headers={PREVIEW_HEADERS}
                  sortBy="^createdDatetime"
                  getData={params =>
                    (isOnFarm ? getOnFarmAccountTransactionsOdata : getAccountTransactionsOdata)(
                      StandardParamsToODataParams(params),
                    )
                  }
                  filter={previewFilter}
                  include={transactionInclude}
                  height={160}
                  pageSize={10}
                  scrollBufferThickness={100}
                  noResultsMessage={LABEL_CONSTANTS.NO_RESULTS_FOUND}
                  onPagingChange={({ offset }) => {
                    setPreviewCount(offset);
                    setResultsStatus(offset > 0 ? 'match' : 'empty');
                  }}
                  buildRow={buildPreviewRow}
                />
              </div>

              <div className="xs-report-results">
                {!isMobilePreviewLoading && mobilePreviewData.length === 0 ? (
                  <div className="result-item">{LABEL_CONSTANTS.NO_RESULTS_FOUND}</div>
                ) : (
                  mobilePreviewData.map((transaction, index) => (
                    <div className="card max-width" key={`report-preview-${transaction.inventoryTransactionId}-${index}`}>
                      <div className="report-preview-card">
                        <div className="report-preview-content">
                          <div className="report-preview-info">
                            <div className="report-preview-field">
                              <label>{TABLE_HEADER_CONSTANTS.TRANSACTION_ID}</label>
                              <span>{transaction.inventoryTransactionId ?? '-'}</span>
                            </div>
                            <div className="report-preview-field">
                              <label>{TABLE_HEADER_CONSTANTS.ANIMAL}</label>
                              <span>{`${transaction.specimen?.animal?.animalId ?? ''} - ${
                                transaction.specimen?.animal?.name ?? ''
                              }`}</span>
                            </div>
                            <div className="report-preview-field">
                              <label>{TABLE_HEADER_CONSTANTS.TYPE}</label>
                              <span>{transaction.transactionType ?? '-'}</span>
                            </div>
                          </div>
                          <div className="report-preview-info">
                            <div className="report-preview-field">
                              <label>{TABLE_HEADER_CONSTANTS.CHANGED_QTY}</label>
                              <span>{transaction.changeQuantity ?? '-'}</span>
                            </div>
                            <div className="report-preview-field">
                              <label>{TABLE_HEADER_CONSTANTS.DATE}</label>
                              <span>{new Date(transaction.createdDatetime).toLocaleDateString()}</span>
                            </div>
                            <div className="report-preview-field">
                              <label>{TABLE_HEADER_CONSTANTS.STATUS}</label>
                              <span>{transaction.status ?? '-'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {isMobilePreviewLoading && <Loader addedSpace loaderSize="small" />}
              </div>
            </div>
          )}
        </div>

        <div className="footer">
          <button
            type="button"
            className={`button blue medium download-report-button ${!canDownload ? 'disabled' : ''}`}
            onClick={handleDownloadReport}
            disabled={isDownloadBlocked}>
            <DownloadSVG />
            {LABEL_CONSTANTS.DOWNLOAD}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default TransactionHistoryReportModal;
